import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LogOut, Users, Settings, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  const [stats, setStats] = useState({
    totalEnrolled: 0,
    totalGrades: 0,
    totalWaitlist: 0
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Fetch consolidated waitlist stats
      const waitlistResponse = await axios.get(`${API}/admin/waitlist-consolidated`);
      const waitlistData = waitlistResponse.data;
      
      // Calculate total waitlist
      const totalWaitlist = waitlistData.reduce((sum, grade) => sum + grade.total_waiting, 0);
      
      // Fetch grade statistics for enrolled count
      const gradeResponse = await axios.get(`${API}/admin/waitlist`);
      const gradeData = gradeResponse.data;
      
      // Calculate total enrolled
      const totalEnrolled = gradeData.reduce((sum, grade) => sum + grade.current_enrolled, 0);
      
      setStats({
        totalEnrolled: totalEnrolled,
        totalGrades: gradeData.length,
        totalWaitlist: totalWaitlist
      });
    } catch (error) {
      console.error("Failed to fetch stats:", error);
      toast.error("Failed to load statistics");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin-login");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0E366A] via-[#1a4d7a] to-[#0E366A]">
      {/* Header */}
      <header className="bg-[#F7B538] border-b border-[#e5a627] sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img 
              src="/lis_logo.png" 
              alt="London Islamic School" 
              className="h-12"
            />
            <div>
              <h1 className="text-xl font-bold text-[#0E366A] font-serif">London Islamic School</h1>
              <p className="text-sm text-[#0E366A] italic">Admin Dashboard</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={handleLogout}
            className="border-[#0E366A] text-[#0E366A] hover:bg-[#0E366A] hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">Welcome, Administrator</h2>
          <p className="text-white/80">Select a management area to continue</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Waitlist Management Card */}
          <Card 
            className="cursor-pointer hover:shadow-xl transition-all duration-300 border-2 hover:border-[#F7B538] bg-white"
            onClick={() => navigate("/admin/waitlist-management")}
          >
            <CardHeader className="text-center pb-4">
              <div className="w-16 h-16 bg-[#F7B538] rounded-full flex items-center justify-center mx-auto mb-4">
                <ClipboardList className="w-8 h-8 text-[#0E366A]" />
              </div>
              <CardTitle className="text-xl text-[#0E366A]">Waitlist Management</CardTitle>
              <CardDescription>
                Manage application waitlists and process interview requests
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• View waitlist by grade</li>
                <li>• Request interviews</li>
                <li>• Accept students</li>
                <li>• Track application positions</li>
              </ul>
            </CardContent>
          </Card>

          {/* Enrolled Students Card */}
          <Card 
            className="cursor-pointer hover:shadow-xl transition-all duration-300 border-2 hover:border-[#F7B538] bg-white"
            onClick={() => navigate("/admin/enrolled")}
          >
            <CardHeader className="text-center pb-4">
              <div className="w-16 h-16 bg-[#F7B538] rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-[#0E366A]" />
              </div>
              <CardTitle className="text-xl text-[#0E366A]">Enrolled Students</CardTitle>
              <CardDescription>
                Manage enrolled students and homeroom assignments
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• View enrolled students</li>
                <li>• Assign homeroom teachers</li>
                <li>• Manage student records</li>
                <li>• View by grade</li>
              </ul>
            </CardContent>
          </Card>

          {/* Grade Management Card */}
          <Card 
            className="cursor-pointer hover:shadow-xl transition-all duration-300 border-2 hover:border-[#F7B538] bg-white"
            onClick={() => navigate("/admin/grade-management")}
          >
            <CardHeader className="text-center pb-4">
              <div className="w-16 h-16 bg-[#F7B538] rounded-full flex items-center justify-center mx-auto mb-4">
                <Settings className="w-8 h-8 text-[#0E366A]" />
              </div>
              <CardTitle className="text-xl text-[#0E366A]">Grade Management</CardTitle>
              <CardDescription>
                Configure grade capacities and view statistics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Set grade capacities</li>
                <li>• View enrollment stats</li>
                <li>• Monitor available spots</li>
                <li>• Manage all grades</li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Quick Stats */}
        <div className="mt-12 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-[#0E366A] mb-4">Quick Overview</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div className="p-4 bg-[#F7B538]/20 rounded-lg border border-[#F7B538]">
              <p className="text-2xl font-bold text-[#0E366A]">{stats.totalEnrolled}</p>
              <p className="text-sm text-gray-600">Total Enrolled</p>
            </div>
            <div className="p-4 bg-[#F7B538]/20 rounded-lg border border-[#F7B538]">
              <p className="text-2xl font-bold text-[#0E366A]">{stats.totalGrades}</p>
              <p className="text-sm text-gray-600">Grades</p>
            </div>
            <div className="p-4 bg-[#F7B538]/20 rounded-lg border border-[#F7B538]">
              <p className="text-2xl font-bold text-[#0E366A]">{stats.totalWaitlist}</p>
              <p className="text-sm text-gray-600">Waitlist</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
